import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
} from 'formik'
import {
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
  TextField,
} from '@material-ui/core'
import { PasswordPolicy } from 'password-sheriff'
import { good as Policy } from 'auth0-password-policies'

import * as UserActionCreators from '../../actions/user'
import CONFIG from '../../config'
import axios from '../../utils/api'
import { login } from '../../utils/AuthService'
import { saveState } from '../../utils/LocalStorage'

import SpinnerSubmitButton from '../controls/buttons/SpinnerSubmitButton'
import InputValidationTooltip from '../controls/input/InputValidationTooltip'
import LoadingStatus from '../controls/LoadingStatusContainer'
import PasswordStrength from './PasswordStrength'

import SmallArrowRight from '../../../assets/images/icons/ico_arrow_small.svg'
import { SHOW_DASHBOARD_INTRO } from '../../constants'

class InitialSetup extends React.Component {
  static propTypes = {
    /* HOC/middleware props */
    classes: PropTypes.object.isRequired,
    /* mapped props */
    users: PropTypes.object.isRequired,
    userActions: PropTypes.object.isRequired,
  }

  static mapStateToProps(state) {
    return {
      users: state.users,
    }
  }

  static mapDispatchToProps(dispatch) {
    return {
      userActions: bindActionCreators(UserActionCreators, dispatch),
    }
  }

  static styles = (theme) => ({
    root: {
      ...theme.introCardRoot,
      minHeight: 'auto',
    },
    content: theme.introCardContent,
    form: {
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
      },
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(3),
      },
    },
    formControl: {
      width: '100%',
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(1),
      },
      '& [class^="PasswordStrength"] ul:first-child': {
        paddingTop: '70px',
      },
    },
    labelRoot: theme.formControlLabelRoot,
    label: theme.label,
    textFieldRoot: theme.loginTextFieldRoot,
    error: {
      color: 'red',
    },
    buttonItem: {
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(1),
      },
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(2),
      },
    },
  })

  componentDidMount() {
    saveState(SHOW_DASHBOARD_INTRO, true)
    this.props.userActions.currentUser()
  }

  validateForm = (values) => {
    const errors = {}

    if (!values.firstname.trim()) {
      errors.firstname = 'First name required'
    }

    if (!values.lastname.trim()) {
      errors.lastname = 'Last name required'
    }

    const policy = new PasswordPolicy(Policy)
    if (!policy.check(values.password)) {
      errors.password = 'Password does not meet password policy'
    }

    if (!values.confirm) {
      errors.confirm = 'Password confirmation required'
    } else if (values.confirm !== values.password) {
      errors.confirm = 'Password confirmation must match'
    }

    return errors
  }

  handleSave = (values, { setErrors, setSubmitting }) => {
    const { email } = this.props.users.me

    const user = {
      given_name: values.firstname,
      family_name: values.lastname,
      password: values.password,
    }

    axios
      .put(`${CONFIG.apiBase}/users/me`, user)
      .then(
        () => {
          login(email, values.password, () => {
            setErrors({ server: 'An error has occurred. Please contact your BlokSense Administrator.' })
          })
        },
        () => {
          setSubmitting(false)
          setErrors({
            server: 'An error has occurred. Please contact your BlokSense Administrator.',
          })
        },
      )
  }

  render() {
    const { classes, users } = this.props

    if (!users.me) {
      return <LoadingStatus />
    }

    return (
      <Card square elevation={3} classes={{ root: classes.root }}>
        <CardContent className={classes.content}>
          <Typography variant="h1">Welcome to BlokSense</Typography>
          <Typography variant="h2">Let&#8217;s fill in a bit more information before we get started.</Typography>

          <Formik
            initialValues={{
              firstname: '',
              lastname: '',
              password: '',
              confirm: '',
            }}
            validate={this.validateForm}
            onSubmit={this.handleSave}
          >
            {(formikProps) => (
              <Form className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="firstname"
                      value={formikProps.values.firstname}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                    >
                      {({ field, form: { touched, errors } }) => (
                        <FormControl className={classes.formControl}>
                          <FormControlLabel
                            classes={{ root: classes.labelRoot, label: classes.label }}
                            label="First Name"
                            labelPlacement="top"
                            control={(
                              <TextField
                                {...field}
                                classes={{ root: classes.textFieldRoot }}
                                error={touched.firstname && Boolean(errors.firstname)}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          />
                          <ErrorMessage name="firstname">
                            { (msg) => <InputValidationTooltip title={msg} /> }
                          </ErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="lastname"
                      value={formikProps.values.lastname}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                    >
                      {({ field, form: { touched, errors } }) => (
                        <FormControl className={classes.formControl}>
                          <FormControlLabel
                            classes={{ root: classes.labelRoot, label: classes.label }}
                            label="Last Name"
                            labelPlacement="top"
                            control={(
                              <TextField
                                {...field}
                                classes={{ root: classes.textFieldRoot }}
                                error={touched.lastname && Boolean(errors.lastname)}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          />
                          <ErrorMessage name="lastname">
                            { (msg) => <InputValidationTooltip title={msg} /> }
                          </ErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="password"
                      value={formikProps.values.password}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                    >
                      {({ field, form: { touched, errors } }) => {
                        const showError = touched.password && Boolean(errors.password)
                        return (
                          <FormControl className={classes.formControl}>
                            <FormControlLabel
                              classes={{ root: classes.labelRoot, label: classes.label }}
                              label="Add a Password"
                              labelPlacement="top"
                              control={(
                                <TextField
                                  {...field}
                                  type="password"
                                  classes={{ root: classes.textFieldRoot }}
                                  error={showError}
                                  fullWidth
                                  variant="outlined"
                                />
                              )}
                            />
                            <ErrorMessage name="password">
                              { (msg) => <InputValidationTooltip title={msg} /> }
                            </ErrorMessage>
                            { showError && <PasswordStrength password={formikProps.values.password} policy={Policy} /> }
                          </FormControl>
                        )
                      }}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="confirm"
                      value={formikProps.values.confirm}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                    >
                      {({ field, form: { touched, errors } }) => (
                        <FormControl className={classes.formControl}>
                          <FormControlLabel
                            classes={{ root: classes.labelRoot, label: classes.label }}
                            label="Confirm Password"
                            labelPlacement="top"
                            control={(
                              <TextField
                                {...field}
                                type="password"
                                classes={{ root: classes.textFieldRoot }}
                                error={touched.confirm && Boolean(errors.confirm)}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          />
                          <ErrorMessage name="confirm">
                            { (msg) => <InputValidationTooltip title={msg} /> }
                          </ErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} className={classes.buttonItem}>
                    <SpinnerSubmitButton
                      onClick={formikProps.handleSubmit}
                      isSubmitting={formikProps.isSubmitting}
                    >
                      Save
                      <img src={SmallArrowRight} alt="→" />
                    </SpinnerSubmitButton>
                    { formikProps.errors.server
                      && <div className={classes.error}>{formikProps.errors.server}</div>}
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    )
  }
}

export default withStyles(InitialSetup.styles)(connect(
  InitialSetup.mapStateToProps,
  InitialSetup.mapDispatchToProps,
)(InitialSetup))
